import { useQuery } from '@apollo/client';
import React, { useMemo } from 'react';
import * as styles from './ControlBarActivity.module.scss';
import Avatar from '../components/Avatar';
import ScrollArea from '../components/ScrollArea';
import SVGIcon from '../components/SVGIcon';
import Timestamp from '../components/Timestamp';
import { gql } from '../graphql/generated';
import type { DocumentType } from '../graphql/generated/gql';
import { AlphaPostActivityType } from '../graphql/generated/graphql';
import archiveIcon from '../icons/non-prefilled/16x16/archive.svg';
import draftsIcon from '../icons/non-prefilled/16x16/drafts.svg';
import editIcon from '../icons/non-prefilled/16x16/edit.svg';
import helpIcon from '../icons/non-prefilled/16x16/help.svg';
import publishIcon from '../icons/non-prefilled/16x16/publish.svg';
import verifiedIcon from '../icons/non-prefilled/16x16/verifiedFilled.svg';
import exhaustiveMatchGuard from '../utils/exhaustiveMatchGuard';

const CONTROL_BAR_ACTIVITY_QUERY = gql(/** @complexity 10 */ `
  query ControlBarActivity($postId: ID!, $beforeAt: Datetime!) {
    alphaPostActivities(postId: $postId, beforeAt: $beforeAt) {
      actedAt
      action
      actors {
        id
        ...Avatar_user
      }
    }
  }
`);

interface ControlBarActivityProps {
  postId: string;
}

const ControlBarActivity = ({ postId }: ControlBarActivityProps) => {
  const beforeAt = useMemo(
    () => new Date(Date.now() + 1000 * 60 * 5).toISOString(),
    [],
  );

  const { data, loading } = useQuery(CONTROL_BAR_ACTIVITY_QUERY, {
    variables: {
      postId,
      beforeAt,
    },
  });

  if (loading) {
    return <div className={styles.container}>Loading...</div>;
  }

  return (
    <ScrollArea className={styles.container}>
      <h3 className={styles.title}>All activities</h3>
      <div>
        {data?.alphaPostActivities.map((activity) => (
          <ActivityItem
            key={`${activity.actedAt}-${activity.action}`}
            activity={activity}
          />
        ))}
      </div>
    </ScrollArea>
  );
};

type ActivityItemProps = {
  activity: DocumentType<
    typeof CONTROL_BAR_ACTIVITY_QUERY
  >['alphaPostActivities'][number];
};

const getIcon = (action: AlphaPostActivityType) => {
  const icon = (children: string, className?: string) => (
    <SVGIcon size={16} className={className}>
      {children}
    </SVGIcon>
  );
  switch (action) {
    case AlphaPostActivityType.Create:
      return icon(draftsIcon);
    case AlphaPostActivityType.Verify:
      return icon(verifiedIcon, styles.green);
    case AlphaPostActivityType.Edit:
      return icon(editIcon, styles.pink);
    case AlphaPostActivityType.Publish:
      return icon(publishIcon, styles.pink);
    case AlphaPostActivityType.Archive:
      return icon(archiveIcon);
    case AlphaPostActivityType.TransferOwner:
    case AlphaPostActivityType.Unarchive:
    case AlphaPostActivityType.Unpublish:
    case AlphaPostActivityType.VerifyExpired:
      return icon(helpIcon);
    default:
      exhaustiveMatchGuard(action);
      return null;
  }
};

const ActivityItem = ({ activity }: ActivityItemProps) => {
  return (
    <div className={styles.activityItem}>
      <div className={styles.activityLine} />
      <div className={styles.activityIcon}>{getIcon(activity.action)}</div>
      <div className={styles.activityTitle}>
        <span className={styles.activityAvatars}>
          {activity.actors?.map((actor) => (
            <Avatar size={20} key="actor.id" user={actor} />
          ))}
        </span>
        <strong>
          {activity.actors?.map((actor) => getFirstName(actor.name)).join(', ')}
        </strong>
        <span className={styles.activityTime}>
          <Timestamp date={activity.actedAt} />
        </span>
      </div>
      <div className={styles.activityDescription}>
        Hey team, FYI I’ve updated our health benefits. The total premiums for
        the most expensive pla...
      </div>
    </div>
  );
};

// TODO: Improve
function getFirstName(name: string): string {
  if (!name) return '';
  const parts = name.split(' ');
  return parts[0] ?? '';
}

export default ControlBarActivity;
